/**
 * Canvas configuration utilities.
 *
 * Provides helpers to determine the runtime environment so the app can
 * behave correctly both inside and outside a Salesforce Canvas iframe.
 */

/**
 * Returns true if the app is currently running inside a Salesforce Canvas iframe.
 *
 * Canvas embeds the app in an `<iframe>`, so `window.self` will differ from
 * `window.top`. We compare object references rather than accessing `.location`
 * to avoid the cross-origin `DOMException` that would be thrown when the parent
 * frame is on a different origin. The try/catch is an additional safety net:
 * if the browser does throw during the reference comparison, it confirms we are
 * inside a cross-origin frame.
 *
 * NOTE: `window` is only available on the client; guard server-side calls.
 */
export const isInSalesforce = (): boolean => {
  // Allow an environment override for local testing only. Prefer the
  // NEXT_PUBLIC_ prefix so the value is available in the browser bundle,
  // but only honor it in local development or test environments.
  const override = getCanvasFlowOverride();
  if (typeof override !== "undefined" && isLocalDev()) {
    return override;
  }

  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin error here means we are definitely inside an iframe.
    return true;
  }
};

/**
 * Reads `NEXT_PUBLIC_CANVAS_FLOW` and returns a boolean override when the
 * env var is explicitly set. Returns `undefined` when the env var is not
 * present or cannot be interpreted deterministically.
 */
export const getCanvasFlowOverride = (): boolean | undefined => {
  const raw = process.env.NEXT_PUBLIC_CANVAS_FLOW;
  if (typeof raw === "undefined") return undefined;
  const val = String(raw).toLowerCase();
  if (val === "true" || val === "1") return true;
  if (val === "false" || val === "0") return false;
  return undefined;
};

/**
 * Returns true when running in a local development or test environment.
 */
export const isLocalDev = (): boolean => {
  const nodeEnv = process.env.NODE_ENV;
  return nodeEnv === "development" || nodeEnv === "test";
};

/**
 * Returns true when mock Canvas data should be used.
 * This is the case during local development OR when running outside of Salesforce.
 */
export const shouldUseMockCanvas = (): boolean => {
  // Allow an environment override to force canvas/non-canvas flows during
  // development. To avoid accidentally enabling mock behavior in production
  // builds, only respect the explicit `NEXT_PUBLIC_CANVAS_FLOW` override when
  // running in a local development or test environment.
  const override = getCanvasFlowOverride();
  if (typeof override !== "undefined" && isLocalDev()) {
    // If override is true, behave as if we're inside Canvas; in local dev
    // we still prefer using fake data, so return `isLocalDev()` in that case.
    if (override === true) return isLocalDev();
    // If override is false, force non-Canvas behaviour (use mocks).
    if (override === false) return true;
  }

  return isLocalDev() || !isInSalesforce();
};
